/**
 * ONE public (unauthenticated) API client for camps and clinics.
 *
 * publicCampsAPI.ts and publicClinicsAPI.ts were the same file with a
 * different noun (Phase B, backend docs/EVENT_PRIMITIVE_CONSOLIDATION.md).
 * They now re-export these types under their original names and bind the
 * functions per type.
 */
import axios from "./axiosInstance";
import config from "./config";
import {
  lookupPublicMemberByEmail,
  verifyPublicMemberByEmail,
  MemberLookupResult as SharedLookupResult,
  MemberVerifyResult as SharedVerifyResult,
} from "./publicMemberLookupAPI";
import { typedEventNames, TypedEventType } from "../utils/typedEvents";

const BASE_URL = config.BASE_URL;

export interface PublicTypedEvent {
  id: string;
  name: string;
  description: string;
  camp_type?: string;
  clinic_type?: string;
  skill_level: string;
  dates: { date: string; start_time: string; end_time: string; location: string }[];
  location: string;
  pricing_options: { label: string; amount: number }[];
  age_min: number | null;
  age_max: number | null;
  max_registrations: number | null;
  current_registrations: number;
  cover_image_url: string | null;
  status: string;
  visibility: string;
  members_only?: boolean;
  allow_other_gender?: boolean;
  registration_deadline: string | null;
  waitlist_enabled: boolean;
  what_to_bring: string | null;
  cancellation_policy: string | null;
}

export interface GuestRegistrationData {
  contact_email: string;
  contact_phone: string;
  contact_name: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  selected_dates?: string[];
  pricing_option?: { label: string; amount: number };
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  medical_info?: string;
  allergies?: string;
  medications?: string;
  dietary_restrictions?: string;
  pickup_authorized?: string[];
  notes?: string;
  waiver_signatures?: any[];
  membership_id?: string;
  // Proof from verify-member that the caller IS this membership (see
  // publicMemberLookupAPI.MemberLookupResult.data.member_token).
  member_token?: string;
}

// Member-identity types and calls now live in ./publicMemberLookupAPI —
// they were byte-identical across camps, clinics and events, wrapped around
// a password-authenticated security boundary. Re-exported here so existing
// imports keep working.
export type {
  MemberLookupAthlete,
  MemberLookupResult,
  MemberVerifyResult,
} from "./publicMemberLookupAPI";

export interface BatchParticipant {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  athlete_id?: string;
}

export interface BatchGuestRegistrationData {
  contact_email: string;
  contact_phone: string;
  contact_name: string;
  participants: BatchParticipant[];
  pricing_option?: { label: string; amount: number };
  selected_dates?: string[];
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  medical_info?: string;
  allergies?: string;
  medications?: string;
  dietary_restrictions?: string;
  pickup_authorized?: string[];
  notes?: string;
  waiver_signatures?: any[];
  membership_id?: string;
  // Proof from verify-member that the caller IS this membership (see
  // publicMemberLookupAPI.MemberLookupResult.data.member_token).
  member_token?: string;
}

export function createPublicTypedEventsAPI(type: TypedEventType) {
  const T = typedEventNames(type);

  const lookupMemberByEmail = (
    tenantId: string,
    email: string
  ): Promise<SharedLookupResult> =>
    lookupPublicMemberByEmail(T.nouns, tenantId, email);

  const verifyMemberByEmail = (
    tenantId: string,
    email: string,
    password: string
  ): Promise<SharedVerifyResult> =>
    verifyPublicMemberByEmail(T.nouns, tenantId, email, password);

  // Get all public camps for a tenant (no auth required)
  const getPublicTypedEvents = async (tenantId: string) => {
    try {
      const response = await axios.get(`${BASE_URL}/public/${T.nouns}`, {
        headers: {
          "x-tenant-id": tenantId,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Get a single public camp by ID (no auth required)
  const getPublicTypedEventById = async (tenantId: string, typedEventId: string) => {
    try {
      const response = await axios.get(`${BASE_URL}/public/${T.nouns}/${typedEventId}`, {
        headers: {
          "x-tenant-id": tenantId,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Register a guest for a camp (no auth required)
  const registerGuestForTypedEvent = async (
    tenantId: string,
    typedEventId: string,
    data: GuestRegistrationData
  ) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/public/${T.nouns}/${typedEventId}/register`,
        data,
        {
          headers: {
            "x-tenant-id": tenantId,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Confirm payment after frontend card confirmation (no auth required)
  const confirmTypedEventPayment = async (
    tenantId: string,
    typedEventId: string,
    paymentIntentId: string
  ) => {
    const response = await axios.post(
      `${BASE_URL}/public/${T.nouns}/${typedEventId}/confirm-payment`,
      { paymentIntentId },
      {
        headers: {
          "x-tenant-id": tenantId,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  };

  // Batch register multiple guests for a camp (no auth required)
  const batchRegisterGuestForTypedEvent = async (
    tenantId: string,
    typedEventId: string,
    data: BatchGuestRegistrationData
  ) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/public/${T.nouns}/${typedEventId}/batch-register`,
        data,
        {
          headers: {
            "x-tenant-id": tenantId,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  return {
    lookupMemberByEmail,
    verifyMemberByEmail,
    getPublicTypedEvents,
    getPublicTypedEventById,
    registerGuestForTypedEvent,
    confirmTypedEventPayment,
    batchRegisterGuestForTypedEvent,
  };
}
